import React, { useState, useEffect } from "react";
import gql from "graphql-tag";
import { useMutation, useQuery, useApolloClient } from "react-apollo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useHistory } from "react-router-dom";
import {
  Column,
  Fieldset,
  Field,
  Control,
  Input,
  Label,
  Button,
  Icon
} from "rbx";

import {
  BooleanInput,
  CustomerSelect,
  LocationSelect,
  SystemCodeSelect,
  BookingCodeSelect
} from "../inputs";

import HorizontalRule from "../../styles/HorizontalRule";
import Loading from "../Loading";

import { CREATE_ROUTE_MUTATION } from "../../graphql/Routes/Routes.graphql";
import { getUTCFormattedDate, ourToast as toast } from "../../utils";
import { ALL_DIRECTIONS_QUERY } from "../../graphql/Directions/Directions.graphql";

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(where: $where) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

const INITIAL_STATE = {
  CustomerRecIdRef: "",
  RouteId: "",
  RouteDescription: "",
  Contract: "",
  PaymentMethodRecIdRef: "",
  StartLocationRecIdRef: "",
  PickupLocationRecIdRef: "",
  DeliveryLocationRecIdRef: "",
  EndingLocationRecIdRef: "",
  LoadTypeRecIdRef: "",
  IndustrialFlag: false,
  MotorOilFlag: false,
  LoadedMiles: "",
  UnloadedMiles: "",
  ChargeForTolls: "",
  Tolls: false,
  LoadTime: "",
  TravelTime: "",
  UnloadTime: "",
  ReturnToTerminalTime: "",
  Status: true,
  BookingCodeRecIdRef: ""
};

const AddRouteForm = () => {
  const [route, setRoute] = useState({ ...INITIAL_STATE });
  const [createRoute, { loading }] = useMutation(CREATE_ROUTE_MUTATION, {
    refetchQueries: ["ALL_ROUTES_VIEW_QUERY"]
  });
  const client = useApolloClient();
  const [codes, setCodes] = useState(null);
  const SystemCodesData = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["BookingCodes", "Route"],
        CodeName_in: ["BookCatg", "LoadType"],
        CodeId_in: ["O", "OE"]
      }
    }
  });

  const history = useHistory();
  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const newRouteCreatedData = await createRoute({
        variables: {
          data: {
            ...route,
            BillToRecIdRef: route.BillToRecIdRef
              ? parseInt(route.BillToRecIdRef)
              : null,
            CustomerRecIdRef: route.CustomerRecIdRef
              ? parseInt(route.CustomerRecIdRef)
              : null,
            PaymentMethodRecIdRef: route.PaymentMethodRecIdRef
              ? parseInt(route.PaymentMethodRecIdRef)
              : null,
            StartLocationRecIdRef: route.StartLocationRecIdRef
              ? parseInt(route.StartLocationRecIdRef)
              : null,
            PickupLocationRecIdRef: route.PickupLocationRecIdRef
              ? parseInt(route.PickupLocationRecIdRef)
              : null,
            DeliveryLocationRecIdRef: route.DeliveryLocationRecIdRef
              ? parseInt(route.DeliveryLocationRecIdRef)
              : null,
            EndingLocationRecIdRef: route.EndingLocationRecIdRef
              ? parseInt(route.EndingLocationRecIdRef)
              : null,
            LoadTypeRecIdRef: route.LoadTypeRecIdRef
              ? parseInt(route.LoadTypeRecIdRef)
              : null,
            LoadedMiles: route.LoadedMiles
              ? parseFloat(route.LoadedMiles)
              : null,
            UnloadedMiles: route.UnloadedMiles
              ? parseFloat(route.UnloadedMiles)
              : null,
            ChargeForTolls: route.ChargeForTolls
              ? parseFloat(route.ChargeForTolls)
              : null,
            LoadTime: route.LoadTime ? parseFloat(route.LoadTime) : null,
            TravelTime: route.TravelTime ? parseFloat(route.TravelTime) : null,
            UnloadTime: route.UnloadTime ? parseFloat(route.UnloadTime) : null,
            ReturnToTerminalTime: route.ReturnToTerminalTime
              ? parseFloat(route.ReturnToTerminalTime)
              : null,
            BookingCodeRecIdRef: route.BookingCodeRecIdRef
              ? parseInt(route.BookingCodeRecIdRef)
              : null,
            EndDate: route.EndDate ? getUTCFormattedDate(route.EndDate) : null,
            StartDate: route.StartDate
              ? getUTCFormattedDate(route.StartDate)
              : null
          }
        }
      });
      toast.success("Route added successfully.");
      history.push(
        `/dispatch/routes/${newRouteCreatedData.data.createRoute.RecId}/details`
      );
    } catch (err) {
      toast.error("Error adding Route.");
    }
  };

  useEffect(() => {
    if (SystemCodesData?.data?.SystemCodes && !codes) {
      const { SystemCodes } = SystemCodesData.data;
      setCodes({
        OrdersBookingCode: SystemCodes.find(
          s => s.CodeId === "O" && s.Category === "BookingCodes"
        ),
        OneWayEmptyType: SystemCodes.find(
          s => s.CodeId === "OE" && s.Category === "Route"
        )
      });
    }
  }, [SystemCodesData, codes]);

  const onChange = async e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const updates = { ...route, [e.target.name]: value };
    // if changing load type to one way empty
    // bind pickup to start and end to delivery
    if (
      e.target.name === "LoadTypeRecIdRef" &&
      parseInt(value) === codes?.OneWayEmptyType?.RecId
    ) {
      updates.PickupLocationRecIdRef = updates.StartLocationRecIdRef;
      updates.EndingLocationRecIdRef = updates.DeliveryLocationRecIdRef;
    }
    // if load type is equal to one way empty bind start / pickup
    // and delivery / ending

    if (parseInt(updates.LoadTypeRecIdRef) === codes?.OneWayEmptyType?.RecId) {
      if (
        ["StartLocationRecIdRef", "PickupLocationRecIdRef"].includes(
          e.target.name
        )
      ) {
        updates.StartLocationRecIdRef = value;
        updates.PickupLocationRecIdRef = value;
      }
      if (
        ["DeliveryLocationRecIdRef", "EndingLocationRecIdRef"].includes(
          e.target.name
        )
      ) {
        updates.DeliveryLocationRecIdRef = value;
        updates.EndingLocationRecIdRef = value;
      }
    }

    // If Pickup Location or Delivery location changed
    if (
      (["PickupLocationRecIdRef", "DeliveryLocationRecIdRef"].includes(
        e.target.name
      ) ||
        (["StartLocationRecIdRef", "EndingLocationRecIdRef"].includes(
          e.target.name
        ) &&
          parseInt(updates.LoadTypeRecIdRef) ===
            codes?.OneWayEmptyType?.RecId)) &&
      updates.DeliveryLocationRecIdRef &&
      updates.PickupLocationRecIdRef
    ) {
      const { data: DirectionData } = await client.query({
        query: ALL_DIRECTIONS_QUERY,
        variables: {
          where: {
            OriginLocationRecIdRef: parseInt(updates.PickupLocationRecIdRef),
            DestinationLocationRecIdRef: parseInt(
              updates.DeliveryLocationRecIdRef
            )
          },
          orderByMulti: ["Priority_ASC"],
          skip: 0,
          first: 1
        }
      });
      if (DirectionData?.Directions?.length > 0) {
        const {
          EmptyMiles,
          LoadedMiles,
          LoadingTime,
          EmptyTravelTime,
          LoadedTravelTime,
          UnloadingTime
        } = DirectionData.Directions[0];
        updates.UnloadedMiles = EmptyMiles;
        updates.LoadedMiles = LoadedMiles;
        updates.LoadTime = LoadingTime;
        updates.TravelTime = EmptyTravelTime + LoadedTravelTime;
        updates.ReturnToTerminalTime = EmptyTravelTime;
        updates.UnloadTime = UnloadingTime;
      } else {
        toast.error(
          "No directions exists between the selected locations. Please add some."
        );
        updates.UnloadedMiles = "";
        updates.LoadedMiles = "";
        updates.LoadTime = "";
        updates.TravelTime = "";
        updates.ReturnToTerminalTime = "";
        updates.UnloadTime = "";
      }
    } else if (e.target.name === "ChargeForTolls") {
      if (value === 0 || value === null || value === "") {
        updates.Tolls = false;
      } else {
        updates.Tolls = true;
      }
    } else if (e.target.name === "Tolls" && (!value || value === "")) {
      updates.ChargeForTolls = 0.0;
    }
    setRoute(updates);
  };

  if (loading || !codes || SystemCodesData.loading) return <Loading />;
  return (
    <React.Fragment>
      <form>
        <Fieldset aria-busy={loading}>
          <Column.Group>
            <Column className="page-head-end">
              <Field kind="group" style={{ justifyContent: "flex-end" }}>
                <Control>
                  <Button color="success" size="small" onClick={handleSubmit}>
                    <Icon>
                      <FontAwesomeIcon icon="save" />
                    </Icon>
                    <span>Save</span>
                  </Button>
                </Control>
              </Field>
            </Column>
          </Column.Group>
          <Column.Group>
            <Column size={4}>
              <Field kind="group">
                <Control expanded>
                  <CustomerSelect
                    icon="globe"
                    label="Customer"
                    name="CustomerRecIdRef"
                    value={route.CustomerRecIdRef}
                    variables={{
                      where: { Active: true, OrderCust: true },
                      orderByMulti: ["CustomerNo_ASC"]
                    }}
                    onChange={onChange}
                  />
                </Control>
              </Field>
              <Field kind="group">
                <Control expanded>
                  <CustomerSelect
                    showHelp
                    icon="money-bill"
                    label="Bill To"
                    name="BillToRecIdRef"
                    showCityAndState={false}
                    value={route.BillToRecIdRef}
                    variables={{
                      where: { Active: true, BillToCust: true },
                      orderByMulti: ["CustomerNo_ASC"]
                    }}
                    onChange={onChange}
                  />
                </Control>
              </Field>
              <Field kind="group">
                <Control>
                  <Label>Industrial</Label>
                  <BooleanInput
                    name="IndustrialFlag"
                    value={route.IndustrialFlag}
                    onChange={onChange}
                  />
                </Control>
                <Control>
                  <Label>Queens</Label>
                  <BooleanInput
                    name="MotorOilFlag"
                    value={route.MotorOilFlag}
                    onChange={onChange}
                  />
                </Control>
                <Control>
                  <Label>No Bill</Label>
                  <BooleanInput
                    name="NonBill"
                    value={route.NonBill}
                    onChange={onChange}
                  />
                </Control>
                <Control>
                  <Label>Tolls</Label>
                  <BooleanInput
                    name="Tolls"
                    value={route.Tolls}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Charge For Tolls</Label>
                  <Input
                    name="ChargeForTolls"
                    step="0.1"
                    type="number"
                    value={route.ChargeForTolls}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
            <Column size={4}>
              <Field kind="group">
                <Control expanded>
                  <Label>Route Description</Label>
                  <Input
                    name="RouteDescription"
                    value={route.RouteDescription}
                    onChange={onChange}
                  />
                </Control>
              </Field>

              <Field>
                <Control expanded>
                  <Label>Contract</Label>
                  <Input
                    autocomplete="new"
                    name="Contract"
                    type="text"
                    value={route.Contract}
                    onChange={onChange}
                  />
                </Control>
              </Field>
              {codes && (
                <Field>
                  <Control expanded>
                    <Label>Booking Code</Label>
                    <BookingCodeSelect
                      label="Booking Code"
                      name="BookingCodeRecIdRef"
                      value={route.BookingCodeRecIdRef}
                      where={{
                        CodeCatgRecIdRef:
                          codes.OrdersBookingCode.RecId /* Orders */
                      }}
                      onChange={onChange}
                    />
                  </Control>
                </Field>
              )}
            </Column>
            <Column size={4}>
              <Field kind="group">
                <Control expanded>
                  <SystemCodeSelect
                    icon="globe"
                    label="Load Type"
                    lookup={{ Category: "Route", Code: "LoadType" }}
                    name="LoadTypeRecIdRef"
                    value={route.LoadTypeRecIdRef}
                    onChange={onChange}
                  />
                </Control>
                <Control>
                  <Label>Status</Label>
                  <BooleanInput
                    name="Status"
                    value={route.Status}
                    onChange={onChange}
                  />
                </Control>
              </Field>
              <Field kind="group">
                <Control expanded>
                  <Label>Load Time</Label>
                  <Input
                    autocomplete="new"
                    name="LoadTime"
                    step="0.25"
                    type="number"
                    value={route.LoadTime}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Travel Time</Label>
                  <Input
                    autocomplete="new"
                    name="TravelTime"
                    type="number"
                    value={route.TravelTime}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Loaded Miles</Label>
                  <Input
                    autocomplete="new"
                    name="LoadedMiles"
                    type="number"
                    value={route.LoadedMiles}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Unload Time</Label>
                  <Input
                    autocomplete="new"
                    name="UnloadTime"
                    type="number"
                    value={route.UnloadTime}
                    onChange={onChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Return Time</Label>
                  <Input
                    autocomplete="new"
                    name="ReturnToTerminalTime"
                    type="number"
                    value={route.ReturnToTerminalTime}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
          </Column.Group>
          <HorizontalRule />
          <Column.Group>
            <Column>
              <Field>
                <Control>
                  <LocationSelect
                    icon="globe"
                    label="Start Location"
                    name="StartLocationRecIdRef"
                    value={route?.StartLocationRecIdRef}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
            <Column>
              <Field>
                <Control>
                  <LocationSelect
                    icon="globe"
                    label="Pickup Location"
                    name="PickupLocationRecIdRef"
                    value={route?.PickupLocationRecIdRef}
                    where={{ PickupLocation: true }}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
            <Column>
              <Field>
                <Control>
                  <LocationSelect
                    icon="globe"
                    label="Delivery Location"
                    name="DeliveryLocationRecIdRef"
                    value={route?.DeliveryLocationRecIdRef}
                    where={{ DeliveryLocation: true }}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
            <Column>
              <Field>
                <Control>
                  <LocationSelect
                    icon="globe"
                    label="Ending Location"
                    name="EndingLocationRecIdRef"
                    value={route?.EndingLocationRecIdRef}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Column>
          </Column.Group>
          <HorizontalRule />
        </Fieldset>
      </form>
    </React.Fragment>
  );
};
AddRouteForm.propTypes = {};

export default AddRouteForm;
